import BookmakerModel from '@mediaplatform/sportal365-odds-utils/types/models/bookmaker.model';
import SelectionModel from '@mediaplatform/sportal365-odds-utils/types/models/selection.model';
import {
  classNames,
  defaultHandleBettingMarketValueClick,
  defaultHandleOddProviderClick,
  defaultHandleUserInteractionClick,
  isMobileUserAgent,
} from '@sportal365/smp-widget-utils';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { DESKTOP, MOBILE } from '../../../../constants/deviceTypes';
import { USER_INTERACTION } from '../../../../constants/entityTypes';
import { GenericLayout, MAX_MOBILE_WIDTH } from '../../../../constants/generic';
import { DROPDOWN_SELECTION_SUBSCRIBE_TYPE } from '../../../../constants/subscribeTypes';
import { MARKETS_WITH_ARBITRARY_SELECTIONS, SUPPORTED_MARKETS } from '../../../../constants/supportedMarkets';
import useResizeObserver from '../../../../hooks/useResizeObserver';
import useRootNodeRef from '../../../../hooks/useRootNodeRef';
import { ILabels, IOddsV2, ISubscriberInfoFn, OddsMarketScope, OddsMarketTypes, ScopeTypes } from '../../../../types';
import { ConfigContext, SMPContainer, SMPProviderLogo, SMPText, styled } from '../../../index';
import { Dropdown } from '../Dropdown';

export interface OddsDisplayProps {
  odds: IOddsV2;
  handleClick: ISubscriberInfoFn;
  marketSelectionDisplay?: boolean;
  isStandalone?: boolean;
  alignment?: GenericLayout;
  isHeader?: boolean;
  oddsMarketScope?: OddsMarketScope;
  areBettingOddsOverlapVisible?: boolean;
  isTransparent?: boolean;
}

interface Options {
  label: string;
  value: string;
}

const extractAvailableMarkets = (odds: IOddsV2, labels?: ILabels | null) => {
  const availableMarkets: Options[] = [];

  odds.forEach((currentOdds) => {
    Object.entries(currentOdds.markets).forEach(([, value]) => {
      if (!value?.type?.code) {
        return;
      }

      const scopeType = value.scope.type.toUpperCase();

      if (
        value.type.code === OddsMarketTypes.ONEXTWO &&
        scopeType !== ScopeTypes.ORDINARY_TIME &&
        !availableMarkets.some((market) => market.value === scopeType)
      ) {
        availableMarkets.push({
          label: labels && labels[value.scope.name] ? labels[value.scope.name] : value.scope.name,
          value: scopeType,
        });

        return;
      }

      if (
        !availableMarkets.some((market) => market.value === value.type.code) &&
        SUPPORTED_MARKETS.includes(value.type.code.toUpperCase())
      ) {
        availableMarkets.push({
          label: labels && labels[value.type.name] ? labels[value.type.name] : value.type.name,
          value: value.type.code,
        });
      }
    });
  });

  return availableMarkets;
};

const extractAvailableValues = (odds: IOddsV2, market: OddsMarketTypes) => {
  const availableValues: Options[] = [];

  odds.forEach((currentOdds) => {
    currentOdds.markets[market]?.selections?.forEach((selection) => {
      const value = selection.value as string;

      if (!availableValues.some((option) => option.value === value)) {
        availableValues.push({ label: value, value });
      }
    });
  });

  return availableValues.sort((a, b) => Number(a.value) - Number(b.value));
};

const MARKETS_WITH_VALUES = [OddsMarketTypes.OVER_UNDER, OddsMarketTypes.GAME_HANDICAP, OddsMarketTypes.FIRST_HALF_GOALS];

export const OddsDisplay: FC<OddsDisplayProps> = ({
  odds: fullOdds,
  handleClick,
  marketSelectionDisplay = true,
  isStandalone = false,
  alignment = GenericLayout.HORIZONTAL,
  isHeader = false,
  oddsMarketScope,
  areBettingOddsOverlapVisible,
  isTransparent = false,
}) => {
  const { dataBettingLogoClickAction, dataBettingLogoOverlap, dataLabels } = useContext(ConfigContext);
  const odds = useMemo(() => {
    if (isStandalone || fullOdds.length <= 1) {
      return fullOdds;
    }

    return [fullOdds[0]];
  }, [fullOdds, isStandalone]);
  const availableMarkets = useMemo(() => extractAvailableMarkets(odds, dataLabels), [odds, dataLabels]);
  const [bettingLogoOverlap, setBettingLogoOverlap] = useState(dataBettingLogoOverlap);
  const [selectedMarket, setSelectedMarket] = useState(availableMarkets[0]?.value as OddsMarketTypes);
  const availableValues = useMemo(() => {
    if (MARKETS_WITH_VALUES.includes(selectedMarket)) {
      return extractAvailableValues(odds, selectedMarket);
    }

    return [];
  }, [selectedMarket, odds]);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(availableValues[0]?.value);
  const [condensedMarketSelectionWrapper, setCondensedMarketSelectionWrapper] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scopeNotFound, setScopeNotFound] = useState(false);
  const measuredRef = useResizeObserver((innerContainerElement) => {
    if (!innerContainerElement.parentElement) {
      return;
    }

    const parentElementOffsetWidth = innerContainerElement.parentElement.offsetWidth;

    setCondensedMarketSelectionWrapper(parentElementOffsetWidth < 640);
    setIsMobile(parentElementOffsetWidth < MAX_MOBILE_WIDTH);
  });

  const { ref } = useRootNodeRef();

  useEffect(() => {
    if (oddsMarketScope) {
      const relevantMarket = availableMarkets.find((market) => market.value === oddsMarketScope);

      if (relevantMarket) {
        setSelectedMarket(relevantMarket.value as OddsMarketTypes);
        setScopeNotFound(false);
      } else {
        setScopeNotFound(true);
      }

      return;
    }

    setSelectedMarket(availableMarkets[0]?.value as OddsMarketTypes);
    setScopeNotFound(false);
  }, [availableMarkets, oddsMarketScope]);

  useEffect(() => {
    setSelectedValue(availableValues[0]?.value);
  }, [availableValues]);

  const arbitrarySelections = useMemo(() => MARKETS_WITH_ARBITRARY_SELECTIONS.includes(selectedMarket), [selectedMarket]);
  const arbitrarySelectionsPerRow = useMemo(() => {
    if (!arbitrarySelections) {
      return undefined;
    }

    if (isMobile) {
      if (
        [OddsMarketTypes.CORRECT_SCORE, OddsMarketTypes.FIRST_HALF_AND_FINAL_RESULT, OddsMarketTypes.FIRST_HALF_GOALS].includes(
          selectedMarket,
        )
      ) {
        return 2;
      }

      if (
        [
          OddsMarketTypes.FIRST_PLAYER_TO_SCORE,
          OddsMarketTypes.PLAYER_TO_SCORE_DURING_GAME,
          OddsMarketTypes.PLAYER_TO_RECEIVE_CARD,
        ].includes(selectedMarket)
      ) {
        return 1;
      }
    }

    return 3;
  }, [arbitrarySelections, isMobile, selectedMarket]);

  useEffect(() => {
    if (arbitrarySelections) {
      setBettingLogoOverlap(false);
    } else {
      setBettingLogoOverlap(dataBettingLogoOverlap);
    }
  }, [arbitrarySelections, dataBettingLogoOverlap, selectedMarket]);

  if (odds.length === 0) {
    return null;
  }

  if (scopeNotFound) {
    if (dataLabels?.no_odds) {
      return (
        <SMPText textAlign='center' variant='contrast'>
          {dataLabels.no_odds}
        </SMPText>
      );
    }

    return null;
  }

  const sendInteractionEvent = () => defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, DROPDOWN_SELECTION_SUBSCRIBE_TYPE);

  const withValuesSelection = MARKETS_WITH_VALUES.includes(selectedMarket);
  const isVerticalAlignment = !arbitrarySelections && alignment === GenericLayout.VERTICAL;

  return (
    <SMPContainer variant={isHeader ? 'sportEntityContainer' : isTransparent ? 'transparentOddsContainer' : 'oddsContainer'} ref={ref}>
      <SMPContainer
        ref={measuredRef}
        css={{
          margin: 'auto',
          px: 0,
          maxWidth: bettingLogoOverlap ? '360px' : '616px',
          height: isVerticalAlignment ? '100%' : undefined,
        }}
      >
        {(marketSelectionDisplay || (!marketSelectionDisplay && withValuesSelection)) && (
          <MarketSelectionWrapper
            className={classNames({
              condensed: condensedMarketSelectionWrapper,
              'with-values-selection': withValuesSelection,
            })}
          >
            {marketSelectionDisplay && (
              <Dropdown
                parentRef={ref}
                value={selectedMarket}
                options={availableMarkets}
                onChange={(value) => {
                  setSelectedMarket(value as OddsMarketTypes);
                  sendInteractionEvent();
                }}
              />
            )}
            {withValuesSelection && (
              <Dropdown
                parentRef={ref}
                value={selectedValue}
                options={availableValues}
                onChange={(value) => {
                  setSelectedValue(value);
                  sendInteractionEvent();
                }}
                strictValueCheck
              />
            )}
          </MarketSelectionWrapper>
        )}
        <SMPContainer
          css={{
            px: 0,
            height: isVerticalAlignment ? '100%' : undefined,
            '& *': {
              boxSizing: 'border-box',
            },
          }}
        >
          {odds.map(({ bookmaker, markets }) => {
            const marketData = markets[selectedMarket];

            if (
              !bookmaker ||
              !marketData ||
              (withValuesSelection && !marketData.selections?.some((selection) => selection.value === selectedValue))
            ) {
              return null;
            }

            const handleOddCoefficientClick = (link: string) =>
              defaultHandleBettingMarketValueClick(handleClick, bookmaker, selectedMarket, link);

            return (
              <OddsContainer
                key={bookmaker.id}
                className={classNames({
                  'arbitrary-selections': arbitrarySelections,
                  'betting-logo-overlap': bettingLogoOverlap,
                  'horizontal-alignment': !arbitrarySelections && alignment === GenericLayout.HORIZONTAL,
                  'vertical-alignment': isVerticalAlignment,
                  'odds-visible': areBettingOddsOverlapVisible,
                })}
              >
                <SMPProviderLogo
                  className={classNames({
                    'disable-interaction': dataBettingLogoClickAction === false,
                  })}
                  href={dataBettingLogoClickAction !== false ? bookmaker.url : undefined}
                  target='_blank'
                  style={{
                    backgroundColor: bookmaker.assets[0].backgroundColor ?? 'transparent',
                    backgroundImage: `url("${bookmaker.assets[0].transparentBackgroundUrl ?? bookmaker.assets[0].logo}")`,
                    borderRadius: '2px',
                  }}
                  onClick={(e) => {
                    if (dataBettingLogoClickAction === false) {
                      e.preventDefault();
                      return;
                    }

                    defaultHandleOddProviderClick(handleClick, { id: bookmaker.id, name: bookmaker.name, url: bookmaker.url });
                  }}
                />
                <SelectionsWrapper
                  arbitrarySelections={arbitrarySelections}
                  arbitrarySelectionsPerRow={arbitrarySelectionsPerRow}
                  bettingLogoOverlap={bettingLogoOverlap}
                >
                  {withValuesSelection
                    ? marketData.selections
                        ?.filter((selection) => selection.value === selectedValue)
                        .map((selection) => (
                          <Selection
                            key={selection.name}
                            bookmaker={bookmaker}
                            handleOddCoefficientClick={handleOddCoefficientClick}
                            selection={selection}
                          />
                        ))
                    : marketData.selections?.map((selection) => (
                        <Selection
                          key={selection.name}
                          bookmaker={bookmaker}
                          handleOddCoefficientClick={handleOddCoefficientClick}
                          selection={selection}
                        />
                      ))}
                </SelectionsWrapper>
              </OddsContainer>
            );
          })}
        </SMPContainer>
      </SMPContainer>
    </SMPContainer>
  );
};

const SelectionsWrapper: FC<
  React.PropsWithChildren<{ arbitrarySelections?: boolean; arbitrarySelectionsPerRow?: number; bettingLogoOverlap?: boolean }>
> = ({ arbitrarySelections, arbitrarySelectionsPerRow, bettingLogoOverlap, children }) => {
  if (arbitrarySelections && arbitrarySelectionsPerRow) {
    return (
      <SelectionContainerArbitraryNumber
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(calc(${
            100 / arbitrarySelectionsPerRow
          }% - calc(var(--sizes-2) * ${arbitrarySelectionsPerRow} - var(--sizes-2))), 1fr))`,
        }}
      >
        {children}
      </SelectionContainerArbitraryNumber>
    );
  }

  if (bettingLogoOverlap) {
    return <SelectionContainer>{children}</SelectionContainer>;
  }

  return <>{children}</>;
};

const Selection: FC<{
  bookmaker: BookmakerModel;
  handleOddCoefficientClick: (link: string) => void;
  selection: SelectionModel;
}> = ({ bookmaker, handleOddCoefficientClick, selection }) => {
  const { dataBettingBrandColors, dataLabels } = useContext(ConfigContext);

  const url = useMemo(() => {
    if (selection.odds === null) {
      return undefined;
    }

    return (
      (selection.urls?.find(({ appType }) => appType === (isMobileUserAgent() ? MOBILE : DESKTOP)) ?? selection.urls?.[0])?.url ??
      bookmaker.url
    );
  }, [bookmaker.url, selection.odds, selection.urls]);

  const branding = {
    backgroundColor: (dataBettingBrandColors && bookmaker.branding?.backgroundColor) || undefined,
    textColor: (dataBettingBrandColors && bookmaker.branding?.textColor) || undefined,
  };

  const selectionName = useMemo(() => {
    const additionalInfoName = selection.additionalInfo?.name;

    if (additionalInfoName) {
      return dataLabels?.[additionalInfoName] ?? additionalInfoName;
    }

    return dataLabels && selection.name && dataLabels[selection.name] ? dataLabels[selection.name] : selection.name;
  }, [dataLabels, selection.additionalInfo?.name, selection.name]);

  return (
    <SelectionCell
      className={classNames({
        'with-betting-brand-colors': dataBettingBrandColors,
      })}
      style={{ backgroundColor: branding.backgroundColor }}
      withOdds={selection.odds !== null}
      href={url}
      target='_blank'
      onClick={() => url && handleOddCoefficientClick(url)}
    >
      <SMPText size='2_5' variant='contrast' style={{ color: branding.textColor, opacity: 0.5 }}>
        {selectionName}
      </SMPText>
      <SMPText size='2_5' variant='contrast' weight='700' style={{ color: branding.textColor }}>
        {selection.odds ?? '-'}
      </SMPText>
    </SelectionCell>
  );
};

const MarketSelectionWrapper = styled.withConfig({ componentId: 'OddsDisplay', displayName: 'MarketSelectionWrapper' })('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: '$2',
  marginBottom: '$3',

  '&.condensed.with-values-selection': {
    '.dropdown-container': {
      width: '50%',
    },
  },
});

const SelectionCell = styled.withConfig({ componentId: 'OddsDisplay', displayName: 'SelectionCell' })('a', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: 'none',
  borderRadius: '$1',
  height: '100%',
  padding: '$1 $2_5',
  backgroundColor: '$oddsSelectionBackgroundColor',
  color: 'inherit',
  textDecoration: 'none',
  outline: 'none',

  '&.with-betting-brand-colors': {
    border: '1px solid $oddsSelectionBorderColor',
  },

  variants: {
    withOdds: {
      true: {
        '&:hover': {
          backgroundColor: '$oddItemHoverColor',
          cursor: 'pointer',
        },
      },
      false: {},
    },
  },
});

const SelectionContainerArbitraryNumber = styled.withConfig({
  componentId: 'OddsDisplay',
  displayName: 'SelectionContainerArbitraryNumber',
})('div');

const SelectionContainer = styled.withConfig({ componentId: 'OddsDisplay', displayName: 'SelectionContainer' })('div', {
  height: '100%',
});

const OddsContainer = styled.withConfig({ componentId: 'OddsDisplay', displayName: 'OddsContainer' })('div', {
  display: 'grid',
  width: '100%',

  '&:not(:last-child)': {
    marginBottom: '$2',
  },

  '&.horizontal-alignment': {
    height: '32px',
  },

  '&.vertical-alignment': {
    height: 'auto',
  },

  '&.arbitrary-selections': {
    gridTemplateColumns: '1fr 3fr',
    gap: '$2',
    height: 'auto',

    [`${SMPProviderLogo}`]: {
      height: '32px',
    },
  },

  '&:not(.betting-logo-overlap)': {
    '&.horizontal-alignment': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))',
      gridTemplateRows: '100%',
      columnGap: '$2',
    },

    '&.vertical-alignment': {
      gridTemplateColumns: '100px',
      gridTemplateRows: 'repeat(auto-fit, 26px)',
      rowGap: '$1',
    },
  },

  '&.betting-logo-overlap': {
    position: 'relative',

    '& > *': {
      position: 'absolute',
      width: '100%',
      left: 0,
      top: 0,
      transition: 'opacity 0.2s',
    },

    [`${SMPProviderLogo}`]: {
      borderRadius: '$0',
      opacity: 1,
      pointerEvents: 'auto',
    },

    [`${SelectionContainer}`]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))',
      gridTemplateRows: '100%',
      columnGap: '$2',
      opacity: 0,
      pointerEvents: 'none',
    },

    '&.vertical-alignment': {
      width: '100px',
      height: '100%',
      alignItems: 'center',

      [`${SMPProviderLogo}`]: {
        position: 'static',
        height: '56px',
      },

      [`${SelectionContainer}`]: {
        gridTemplateColumns: '100%',
        gridTemplateRows: 'repeat(2, 26px)',
        alignContent: 'center',
        rowGap: '$1',

        [`${SelectionCell}`]: {
          height: '26px',
        },
      },
    },

    '&.odds-visible': {
      [`${SMPProviderLogo}`]: {
        opacity: 0,
        pointerEvents: 'none',
      },

      [`${SelectionContainer}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
  },

  [`${SelectionContainerArbitraryNumber}`]: {
    display: 'grid',
    gap: '$2',

    [`${SelectionCell}`]: {
      height: '32px',
    },
  },
});

export default OddsDisplay;
